import { ReactNode, useRef, useEffect, useState, CSSProperties } from 'react';
import { cn } from '@/lib/utils';

type AnimationVariant = 'fadeUp' | 'fadeDown' | 'fadeLeft' | 'fadeRight' | 'scale' | 'blur' | 'slide';

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  variant?: AnimationVariant;
  delay?: number;
  duration?: number;
  threshold?: number;
  once?: boolean;
  stagger?: boolean;
  staggerDelay?: number;
}

const hiddenStyles: Record<AnimationVariant, CSSProperties> = {
  fadeUp: { opacity: 0, transform: 'translateY(12px)' },
  fadeDown: { opacity: 0, transform: 'translateY(-12px)' },
  fadeLeft: { opacity: 0, transform: 'translateX(-16px)' },
  fadeRight: { opacity: 0, transform: 'translateX(16px)' },
  scale: { opacity: 0, transform: 'scale(0.8)' },
  blur: { opacity: 0, filter: 'blur(10px)' },
  slide: { opacity: 0, transform: 'translateX(-24px)' },
};

const visibleStyles: CSSProperties = {
  opacity: 1,
  transform: 'translateY(0) translateX(0) scale(1)',
  filter: 'blur(0px)',
};

export const ScrollReveal = ({
  children,
  className,
  variant = 'fadeUp',
  delay = 0,
  duration = 0.6,
  threshold = 0.2,
  once = true,
}: ScrollRevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [initiallyInView, setInitiallyInView] = useState<boolean | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold }
    );

    // Check if already in viewport on mount
    const rect = el.getBoundingClientRect();
    const inView = rect.top < window.innerHeight && rect.bottom > 0;
    setInitiallyInView(inView);
    if (inView) setIsVisible(true);

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, once]);

  const skipAnimation = initiallyInView === true;

  const style: CSSProperties = skipAnimation
    ? visibleStyles
    : isVisible
    ? {
        ...visibleStyles,
        transition: `opacity ${duration}s cubic-bezier(0.25,0.1,0.25,1) ${delay}s, transform ${duration}s cubic-bezier(0.25,0.1,0.25,1) ${delay}s, filter ${duration}s cubic-bezier(0.25,0.1,0.25,1) ${delay}s`,
      }
    : hiddenStyles[variant];

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
};

// Container for staggered children animations
interface StaggerContainerProps {
  children: ReactNode;
  className?: string;
  staggerDelay?: number;
  threshold?: number;
  once?: boolean;
}

export const StaggerContainer = ({
  children,
  className,
  staggerDelay = 0.1,
  threshold = 0.1,
  once = true,
}: StaggerContainerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, once]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ '--stagger-delay': `${staggerDelay}s`, '--stagger-visible': isVisible ? '1' : '0' } as CSSProperties}
      data-stagger-visible={isVisible ? 'true' : 'false'}
    >
      {children}
    </div>
  );
};

// Individual stagger item
interface StaggerItemProps {
  children: ReactNode;
  className?: string;
  variant?: AnimationVariant;
}

let staggerCounter = 0;

export const StaggerItem = ({
  children,
  className,
  variant = 'fadeUp',
}: StaggerItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !el.parentElement) return;
    const parent = el.parentElement;
    const siblings = Array.from(parent.children);
    setIndex(siblings.indexOf(el));

    // Observe parent's data-stagger-visible attribute
    const checkVisibility = () => {
      setIsVisible(parent.getAttribute('data-stagger-visible') === 'true');
    };
    checkVisibility();

    const observer = new MutationObserver(checkVisibility);
    observer.observe(parent, { attributes: true, attributeFilter: ['data-stagger-visible'] });
    return () => observer.disconnect();
  }, []);

  const parent = ref.current?.parentElement;
  const staggerDelay = parent ? parseFloat(getComputedStyle(parent).getPropertyValue('--stagger-delay') || '0.1') : 0.1;

  const style: CSSProperties = isVisible
    ? {
        ...visibleStyles,
        transition: `opacity 0.5s cubic-bezier(0.25,0.1,0.25,1) ${index * staggerDelay}s, transform 0.5s cubic-bezier(0.25,0.1,0.25,1) ${index * staggerDelay}s`,
      }
    : hiddenStyles[variant];

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
};

// Section wrapper with reveal animation
interface RevealSectionProps {
  children: ReactNode;
  className?: string;
  id?: string;
  variant?: AnimationVariant;
  delay?: number;
}

export const RevealSection = ({
  children,
  className,
  id,
  variant = 'fadeUp',
  delay = 0,
}: RevealSectionProps) => {
  return (
    <ScrollReveal variant={variant} delay={delay} className={cn('w-full', className)}>
      <section id={id} className="w-full">
        {children}
      </section>
    </ScrollReveal>
  );
};

// Text reveal with character or word animation
interface TextRevealProps {
  text: string;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span';
  splitBy?: 'word' | 'character';
  staggerDelay?: number;
}

export const TextReveal = ({
  text,
  className,
  as: Component = 'p',
  splitBy = 'word',
  staggerDelay = 0.03,
}: TextRevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const items = splitBy === 'word' ? text.split(' ') : text.split('');
  const separator = splitBy === 'word' ? '\u00A0' : '';

  return (
    <Component ref={ref as any} className={cn('overflow-hidden', className)}>
      {items.map((item, i) => (
        <span
          key={i}
          className="inline-block"
          style={{
            transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
            opacity: isVisible ? 1 : 0,
            transition: `transform 0.5s cubic-bezier(0.25,0.1,0.25,1) ${i * staggerDelay}s, opacity 0.5s cubic-bezier(0.25,0.1,0.25,1) ${i * staggerDelay}s`,
          }}
        >
          {item}{separator}
        </span>
      ))}
    </Component>
  );
};

// Counter animation for stats
interface CounterRevealProps {
  value: number;
  suffix?: string;
  prefix?: string;
  className?: string;
  duration?: number;
}

export const CounterReveal = ({
  value,
  suffix = '',
  prefix = '',
  className,
  duration = 2,
}: CounterRevealProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {isVisible && <Counter from={0} to={value} duration={duration} />}
      {suffix}
    </span>
  );
};

// Simple counter component
const Counter = ({ from, to, duration }: { from: number; to: number; duration: number }) => {
  const [count, setCount] = useState(from);

  useEffect(() => {
    const startTime = Date.now();
    const endTime = startTime + duration * 1000;

    const updateCount = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / (duration * 1000), 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(from + (to - from) * easeOut));
      if (now < endTime) requestAnimationFrame(updateCount);
    };

    requestAnimationFrame(updateCount);
  }, [from, to, duration]);

  return <span>{count.toLocaleString()}</span>;
};
